// Grow the board on demand, with the parameters that actually matter.
//
// The board growing is the thing this game does that nothing else does, and
// until this existed the only way to see it was to win a war: `reveal-region`
// opens a region a *level* already described, and `next-stage` is gated on a
// side having no move. So neither could answer "make it bigger so I can look at
// the tile animation / the label placement / the instanced pools".
//
// The parameters are the ones that change what you see: which way it grows, how
// far, and whether anything is standing on it. Everything else — where exactly,
// in what shape — is the level designer's business and not a debugging
// question.

import { useState } from "react";

import { KING, makePiece, PAWN, ROOK, square } from "src/shared/board";
import type { DevDispatch } from "../DevMenu";
import { Btn, HoverCard, Segmented, Slider } from "../kit";
import { useGame } from "../useGame";
import { useBesidePanel, Loose, Note, Row, Fold } from "./parts";

type Edge = "north" | "south" | "east" | "west";

/** What `grow` stands on the ground it opens. */
interface Camp {
  force: boolean;
  scrap: boolean;
  tough: number;
  /** Armour on every piece it stands up (`Position.plating`), `tough`'s twin. */
  plated: number;
  /** Stand it on the near ring rather than the far one. See `grow`. */
  near: boolean;
}

const EDGES: readonly { value: Edge; label: string; testId: string }[] = [
  { value: "north", label: "N", testId: "dev-grow-north" },
  { value: "south", label: "S", testId: "dev-grow-south" },
  { value: "east", label: "E", testId: "dev-grow-east" },
  { value: "west", label: "W", testId: "dev-grow-west" }
];

export function GrowBoard({ dispatch }: { dispatch: DevDispatch }) {
  const game = useGame();
  const [side, setSide] = useState<Edge>("north");
  const [depth, setDepth] = useState(4);
  const [force, setForce] = useState(false);
  const [scrap, setScrap] = useState(false);
  const [tough, setTough] = useState(0);
  const [plated, setPlated] = useState(0);
  const panel = useBesidePanel();

  /**
   * Open ground, and optionally stand a camp on it.
   *
   * Takes overrides so a one-click button can supply a whole setup without
   * the panel's four controls having to be driven into place first — and
   * without a second copy of the placement.
   */
  const grow = (over: Partial<Camp> = {}): void => {
    const camp: Camp = { force, scrap, tough, plated, near: false, ...over };
    const { minFile, maxFile, minRank, maxRank } = game.board.facts().bounds;
    const cells: number[] = [];
    const pieces: [number, number][] = [];
    const heaps: [number, number][] = [];

    // Along the whole edge, so the board keeps a shape somebody would
    // recognise rather than growing a tongue.
    for (let step = 1; step <= depth; step++) {
      for (let across = minFile; across <= maxFile; across++) {
        const rank = side === "north" ? maxRank + step : minRank - step;
        if (side === "north" || side === "south") cells.push(square(across, rank));
      }
      for (let across = minRank; across <= maxRank; across++) {
        const file = side === "east" ? maxFile + step : minFile - step;
        if (side === "east" || side === "west") cells.push(square(file, across));
      }
    }

    if (camp.force || camp.scrap) {
      // On the far edge of the new ground, which is where a camp would land —
      // **unless the caller wants it within reach**. A camp four ranks out is
      // realistic and is a ten-rank walk before anything can be tried on it,
      // which for a tool whose whole job is "show me what this does" is the
      // difference between using it and not. `near` puts the army on the first
      // ring instead, with the rest of the new ground behind it as the room a
      // body needs to move at all.
      const width = Math.max(1, Math.floor(cells.length / depth));
      const strip = camp.near ? cells.slice(0, width) : cells.slice(-width);
      const spot = (index: number): number => strip[Math.min(index, strip.length - 1)];
      if (camp.force) {
        // A king and a couple of bodies: enough to be an army the stage
        // machinery would accept, since a camp with no king can never be
        // cleared. Under a side that is not on the board yet.
        const flag = game.board.status().sides;
        pieces.push([spot(0), makePiece(flag, KING)]);
        pieces.push([spot(2), makePiece(flag, ROOK)]);
        pieces.push([spot(4), makePiece(flag, PAWN)]);
      }
      if (camp.scrap) {
        for (let i = 1; i < Math.min(strip.length, 10); i += 2) heaps.push([spot(i), 6]);
      }
    }

    dispatch({
      type: "dev-grow",
      cells,
      pieces,
      heaps,
      tough: camp.tough,
      plated: camp.plated
    });
  };

  return (
    <Fold id="grow" title="Grow the board">
      <Loose>
        <Segmented small value={side} options={EDGES} onChange={setSide} />
        <Note>{depth} deep</Note>
      </Loose>
      <Slider
        size="small"
        min={1}
        max={12}
        value={depth}
        data-testid="dev-grow-depth"
        onChange={(_, value) => setDepth(typeof value === "number" ? value : depth)}
      />
      <Row>
        <Btn
          small
          variant={force ? "primary" : "default"}
          data-testid="dev-grow-force"
          aria-pressed={force}
          onClick={() => setForce((was) => !was)}
        >
          With a camp
        </Btn>
        <Btn
          small
          variant={scrap ? "primary" : "default"}
          data-testid="dev-grow-scrap"
          aria-pressed={scrap}
          onClick={() => setScrap((was) => !was)}
        >
          With scrap
        </Btn>
      </Row>
      {/* **How tough the camp is** (§27). A capture kills only when the
          attacker's damage meets the target's health, so a camp on ordinary
          `PIECE_HEALTH` plays exactly as it always did and one at twelve has to
          be ground down — a pawn takes twelve turns over it and a queen two.
          Rides with the camp toggle: it is a fact about the army being stood
          up, and above a button that would ignore it, it is a control that
          does nothing. */}
      {force && (
        <>
          <Note>{tough === 0 ? "ordinary health" : `${tough} health`}</Note>
          <HoverCard
            placement="right"
            beside={panel}
            title="Camp health"
            card="Health for every piece the camp stands up, whatever its rank. Nought leaves them on PIECE_HEALTH."
          >
            <Slider
              size="small"
              min={0}
              max={30}
              value={tough}
              data-testid="dev-grow-tough"
              onChange={(_, value) => setTough(typeof value === "number" ? value : tough)}
            />
          </HoverCard>
          {/* **And how much armour it wears**, one layer up: plate a shot has to
              chew through before it reaches the health above. The only door
              onto `Position.plating` — nothing in a run earns it yet — so
              without this the mechanic cannot be looked at. */}
          <Note>{plated === 0 ? "no armour" : `${plated} armour`}</Note>
          <HoverCard
            placement="right"
            beside={panel}
            title="Camp armour"
            card="Plate on every piece the camp stands up, above its health. Turret fire and strikes chew through it first; a capture ignores it. Nought is none."
          >
            <Slider
              size="small"
              min={0}
              max={30}
              value={plated}
              data-testid="dev-grow-plated"
              onChange={(_, value) => setPlated(typeof value === "number" ? value : plated)}
            />
          </HoverCard>
        </>
      )}
      <Btn small variant="primary" data-testid="dev-grow-go" onClick={() => grow()}>
        Open the ground
      </Btn>
    </Fold>
  );
}
